/**
 * MissingEntrySearchModal
 *
 * A self-contained modal component that handles:
 * - Searching audit logs for a missing "In" or "Out" attendance entry
 * - Face angle selection
 * - Similarity threshold slider
 * - Displaying results (AuditTile grid)
 * - Rematch & Mark Attendance / Mark Attendance actions
 * - Deleting the original missing entry on success
 *
 * Used in both Audit.js and Intrusion.js
 */
	import React, { useState, useEffect, useRef, useCallback } from 'react';
	import {
	  Box,
	  Typography,
	  CircularProgress,
	  Button,
	  Chip,
	  Stack,
	  Dialog,
	  DialogTitle,
	  DialogContent,
	  DialogActions,
	  IconButton,
	  FormControl,
	  Select,
	  MenuItem,
	  InputLabel,
	  Slider,
	  Grid,
	} from '@mui/material';
	import { useTheme } from '@mui/material/styles';
	import useMediaQuery from '@mui/material/useMediaQuery';
	import {
	  Close,
	  Search,
	  Refresh as RefreshIcon,
	  EventAvailable,
	} from '@mui/icons-material';

import {
  getPersonFaces,
  getFaceImage,
  getAuditImageBlob,
  getAuditLogs,
  findPersonCombined,
  unmatchToMatch,
  markAuditAttendance,
} from '../utils/api';
import { peek as peekAuditImage } from '../services/auditImageCache';
import { formatDate } from '../utils/dateUtils';
import { AuditTile, getResultMeta } from './Audit';

const primary = '#40518A';

// ---------------------------------------------------------------------------
// Pure helpers (duplicated from Audit.js so this file is self-contained)
// ---------------------------------------------------------------------------
/** Shape matches Audit main view: AuditTile reads `capturedTs.iso` for the footer time. */
const getCapturedTimestamp = (log) => {
  const meta = log.request_metadata || {};
  const raw =
    log.captured_at ||
    meta.captured_at ||
    log.event_timestamp ||
    log.created_at;
  if (raw == null || raw === '') return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return { iso: d.toISOString() };
};

const formatAngleLabel = (angle, metadata = {}) => {
  if (metadata && metadata.custom_angle_label) return metadata.custom_angle_label;
  if (!angle) return '';
  const s = String(angle).toLowerCase();
  if (s === 'left_profile') return 'left';
  if (s === 'right_profile') return 'right';
  if (s === 'custom_angle') return 'custom';
  return s.replace('_', ' ');
};

const normalizeToUTC = (timeStr) => {
  if (!timeStr) return null;
  if (timeStr.endsWith('Z') || timeStr.match(/[+-]\d{2}:?\d{2}$/)) {
    return new Date(timeStr);
  }
  const normalized = timeStr.includes('T') ? timeStr + 'Z' : timeStr.replace(' ', 'T') + 'Z';
  return new Date(normalized);
};

const blobToDataUrl = (blob) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(blob);
  });

const INVALID_FIND_SCORE = -1;

/** Score for ranking find-person results: unmatched uses embedding similarity; known rows share query-image recognition score from API. */
const getFindPersonComparableScore = (log, queryRecognitionScore) => {
  const u = Number(log?.unknown_match_similarity);
  if (Number.isFinite(u)) return u;
  if (
    String(log?.find_person_source) === 'known' &&
    queryRecognitionScore != null &&
    Number.isFinite(Number(queryRecognitionScore))
  ) {
    return Number(queryRecognitionScore);
  }
  const c = Number(log?.confidence_score);
  if (Number.isFinite(c)) return c;
  return INVALID_FIND_SCORE;
};

/** Pre-select only the single best-scoring log (ties: first in result order, i.e. most recent). */
const initialSelectedAuditIdsFromResults = (logs, queryRecognitionScore) => {
  const withId = Array.isArray(logs) ? logs.filter((l) => l?.audit_id) : [];
  if (withId.length === 0) return new Set();
  const qs = queryRecognitionScore != null ? Number(queryRecognitionScore) : NaN;
  const useQuery = Number.isFinite(qs);
  let maxScore = INVALID_FIND_SCORE;
  for (const l of withId) {
    const s = getFindPersonComparableScore(l, useQuery ? qs : NaN);
    if (s > maxScore) maxScore = s;
  }
  if (maxScore === INVALID_FIND_SCORE) return new Set();
  const chosen = withId.find((l) => getFindPersonComparableScore(l, useQuery ? qs : NaN) === maxScore);
  return chosen?.audit_id ? new Set([String(chosen.audit_id)]) : new Set();
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
/**
 * @param {object}   props
 * @param {boolean}  props.open              - Whether the modal is visible
 * @param {object}   props.missingEvent      - The missing attendance log object
 * @param {string}   props.personThumbnail   - Person thumbnail data-URL
 * @param {object[]} props.recentEvents      - All recent events for this person
 * @param {string}   props.personId          - Person ID
 * @param {function} props.onClose           - Called when the modal should close (no action)
 * @param {function} props.onSuccess         - Called after a successful action (reload, etc.)
 * @param {function} props.showMessage       - (message, severity) → shows a snackbar in the host
 * @param {function} [props.onLogClick]      - Optional: host timing-dialog opener
 * @param {function} [props.onSearchIconClick] - Optional: host find-person-for-tile opener
 * @param {function} [props.onOpenManualMarkAttendance] - Optional: host opener for shared Mark Attendance modal
 */
	const MissingEntrySearchModal = ({
	  open,
	  missingEvent,
	  personThumbnail,
	  recentEvents,
	  personId,
	  onClose,
	  onSuccess,
	  showMessage,
	  onLogClick = null,
	  onSearchIconClick = null,
	  onOpenManualMarkAttendance = null,
	}) => {
	  const theme = useTheme();
	  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
	  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
	  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

	  // ── State ─────────────────────────────────────────────────────────────────
	  const [firstLeft, setFirstLeft] = useState(null);
	  const [firstRight, setFirstRight] = useState(null);
	  const [camera, setCamera] = useState(null);

  const [personFaces, setPersonFaces] = useState([]);
  const [selectedAngle, setSelectedAngle] = useState('');
  const [selectedFaceImage, setSelectedFaceImage] = useState(null);

  const [threshold, setThreshold] = useState('0.3');

  const [searchLoading, setSearchLoading] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [rangeUnmatchedLogs, setRangeUnmatchedLogs] = useState([]);
  const [rangeUnmatchedLoading, setRangeUnmatchedLoading] = useState(false);
  const [imageCache, setImageCache] = useState({});
  const [summary, setSummary] = useState({
    known_count: 0,
    possible_match_count: 0,
    unknown_count: 0,
    total_count: 0,
  });
  const [selectedLogs, setSelectedLogs] = useState(new Set());

  const [rematchLoading, setRematchLoading] = useState(false);
  const [markLoading, setMarkLoading] = useState(false);

  const skipAutoRef = useRef(false);
  const initialSearchPendingRef = useRef(false);
  const [hasSearched, setHasSearched] = useState(false);

  // ── Reset stale state when modal closes (component stays mounted) ─────────
  useEffect(() => {
    if (open) return;

    setSelectedFaceImage(null);
    setPersonFaces([]);
    setSelectedAngle('');
    setFirstLeft(null);
    setFirstRight(null);
    setCamera(null);
    setResults([]);
    setRangeUnmatchedLogs([]);
    setImageCache({});
    setSummary({
      known_count: 0,
      possible_match_count: 0,
      unknown_count: 0,
      total_count: 0,
    });
    setSelectedLogs(new Set());
    setThreshold('0.3');
    setHasSearched(false);
    setSearchLoading(false);
    setResultsLoading(false);
    setRangeUnmatchedLoading(false);
    setRematchLoading(false);
    setMarkLoading(false);
    skipAutoRef.current = false;
    initialSearchPendingRef.current = false;
  }, [open]);

  // ── Initialise when modal opens ───────────────────────────────────────────
  useEffect(() => {
    if (!open || !missingEvent || !Array.isArray(recentEvents)) return;

    // Clear stale preview immediately so the previous person's image doesn't linger.
    setSelectedFaceImage(null);
    setPersonFaces([]);
    setSelectedAngle('');

    (async () => {
      // Derive firstLeft / firstRight from recentEvents.
      // Use UTC-normalized parsing everywhere so range selection matches display/search behavior.
      const realEvents = recentEvents
        .filter((e) => !e?.is_missing_entry)
        .filter((e) => normalizeToUTC(String(e?.created_at || '').trim()));
      const sorted = [...realEvents].sort((a, b) => {
        const aTs = normalizeToUTC(String(a?.created_at || '').trim())?.getTime() || 0;
        const bTs = normalizeToUTC(String(b?.created_at || '').trim())?.getTime() || 0;
        return aTs - bTs;
      });
      const missingTime = normalizeToUTC(String(missingEvent?.created_at || '').trim()) || new Date();

      let left = null;
      for (let i = sorted.length - 1; i >= 0; i--) {
        const ts = normalizeToUTC(String(sorted[i]?.created_at || '').trim());
        if (ts && ts < missingTime) { left = sorted[i]; break; }
      }
      let right = null;
      for (let i = 0; i < sorted.length; i++) {
        const ts = normalizeToUTC(String(sorted[i]?.created_at || '').trim());
        if (ts && ts > missingTime) { right = sorted[i]; break; }
      }
      if (!left) {
        // Business rule: if "from" side event is missing, start search from 9:00 AM (same day).
        const d = new Date(missingTime);
        d.setHours(9, 0, 0, 0);
        // Safety: in rare case missing entry is before 9 AM, keep range valid.
        if (d >= missingTime) {
          d.setTime(missingTime.getTime() - 60 * 60 * 1000);
        }
        left = { created_at: d.toISOString() };
      }
      if (!right) {
        const d = new Date(missingTime.getTime() + 60 * 60 * 1000);
        right = { created_at: d.toISOString() };
      }

      setFirstLeft(left);
      setFirstRight(right);
      setCamera(missingEvent.action === 'check_in' ? 'IN' : 'OUT');

      // Reset results
      setResults([]);
      setRangeUnmatchedLogs([]);
      setImageCache({});
      setSummary({
        known_count: 0,
        possible_match_count: 0,
        unknown_count: 0,
        total_count: 0,
      });
      setSelectedLogs(new Set());
      setThreshold('0.3');
      setHasSearched(false);

      // Load face angles
      let faces = [];
      let bestFaceId = '';
      let bestFaceImage = personThumbnail;

      if (personId) {
        try {
          const raw = await getPersonFaces(personId);
          faces = Array.isArray(raw) ? raw.filter((f) => !f.deleted_at) : [];
          const sortByPose = (a, b) => Number(b?.pose_quality || 0) - Number(a?.pose_quality || 0);
          const fronts = faces
            .filter((f) => String(f?.face_angle || '').toLowerCase() === 'front')
            .sort(sortByPose);
          const preferred = fronts[0] || faces.sort(sortByPose)[0];
          if (preferred?.face_id) {
            bestFaceId = preferred.face_id;
            try {
              const blob = await getFaceImage(preferred.face_id);
              bestFaceImage = await blobToDataUrl(blob);
            } catch (_) {
              bestFaceImage = personThumbnail;
            }
          }
        } catch (_) {
          // ignore – fall back to thumbnail
        }
      }

      setPersonFaces(faces);
      setSelectedAngle(bestFaceId);
      setSelectedFaceImage(bestFaceImage);

      // Block the threshold/angle auto-search effect from firing during init,
      // but mark that an initial search should run automatically once ready.
      skipAutoRef.current = true;
      initialSearchPendingRef.current = true;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, missingEvent, personId]);

  // ── Auto-run the initial search once the modal has finished initialising ───
  // Runs the default-threshold search on open so results are visible without
  // having to click "Find Person".
  useEffect(() => {
    if (
      !open ||
      !initialSearchPendingRef.current ||
      searchLoading ||
      resultsLoading ||
      !personThumbnail ||
      !firstLeft ||
      !firstRight
    ) return;

    initialSearchPendingRef.current = false;
    skipAutoRef.current = false;
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, firstLeft, firstRight, selectedFaceImage, personThumbnail]);

  // ── Auto-search on threshold / angle change ───────────────────────────────
  useEffect(() => {
    if (
      !open ||
      skipAutoRef.current ||
      searchLoading ||
      resultsLoading ||
      !personThumbnail ||
      !firstLeft ||
      !firstRight
    ) return;

    if (!hasSearched) return;

    const delay = selectedFaceImage !== null ? 800 : 500;
    const id = setTimeout(() => {
      if (selectedFaceImage || personThumbnail) {
        skipAutoRef.current = false;
        handleSearch();
      }
    }, delay);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threshold, selectedFaceImage, open]);

  // ── Angle change ──────────────────────────────────────────────────────────
  const handleAngleChange = async (faceId) => {
    setSelectedAngle(faceId);
    if (!faceId) { setSelectedFaceImage(personThumbnail); return; }
    setSelectedFaceImage(null);
    try {
      const blob = await getFaceImage(faceId);
      setSelectedFaceImage(await blobToDataUrl(blob));
    } catch (err) {
      console.error('Error loading face image:', err);
      showMessage('Failed to load face image', 'error');
      setSelectedFaceImage(personThumbnail);
    }
  };

  const preloadAuditImages = useCallback(() => {}, []);

  // Fetch all unmatched logs for selected time range (shown below threshold section).
  const loadRangeUnmatchedLogs = useCallback(async () => {
    if (!open || !firstLeft || !firstRight || !camera) return;
    const startUTC = normalizeToUTC(String(firstLeft.created_at || '').trim());
    const endUTC = normalizeToUTC(String(firstRight.created_at || '').trim());
    if (!startUTC || !endUTC || Number.isNaN(startUTC.getTime()) || Number.isNaN(endUTC.getTime())) return;
    if (startUTC >= endUTC) return;
    try {
      setRangeUnmatchedLoading(true);
      const resp = await getAuditLogs({
        operation_type: 'recognize',
        start_time: startUTC.toISOString().slice(0, 16),
        end_time: endUTC.toISOString().slice(0, 16),
        camera: String(camera || '').toLowerCase(),
        status: 'unmatch',
        limit: 500,
        offset: 0,
      });
      const logs = Array.isArray(resp?.logs) ? resp.logs : [];
      setRangeUnmatchedLogs(logs);
      preloadAuditImages(logs);
    } catch (_) {
      setRangeUnmatchedLogs([]);
    } finally {
      setRangeUnmatchedLoading(false);
    }
  }, [open, firstLeft, firstRight, camera, preloadAuditImages]);

  useEffect(() => {
    loadRangeUnmatchedLogs();
  }, [loadRangeUnmatchedLogs]);

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(async () => {
    const searchImage = selectedFaceImage || personThumbnail;
    if (!searchImage) { showMessage('Person image is required for search', 'warning'); return; }
    if (!firstLeft || !firstRight) { showMessage('Time range information is missing', 'warning'); return; }

    const startUTC = normalizeToUTC(String(firstLeft.created_at || '').trim());
    const endUTC   = normalizeToUTC(String(firstRight.created_at || '').trim());

    if (!startUTC || !endUTC || isNaN(startUTC) || isNaN(endUTC)) {
      showMessage('Invalid time range: could not parse timestamps', 'error'); return;
    }
    if (startUTC >= endUTC) {
      showMessage('Invalid time range: start must be before end', 'error'); return;
    }

    try {
      setSearchLoading(true);
      setResultsLoading(true);
      setResults([]);
      skipAutoRef.current = true;

      const resp = await findPersonCombined({
        image_base64: searchImage,
        start_time: startUTC.toISOString().slice(0, 16),
        end_time:   endUTC.toISOString().slice(0, 16),
        name: '',
        camera,
        similarity_threshold: Math.max(0, Math.min(1, Number(threshold) || 0.3)),
        limit: 100,
        offset: 0,
      });

      const logs       = Array.isArray(resp?.combined_matches) ? resp.combined_matches : [];
      const knownCnt   = Number(resp?.known_count || 0);
      const possibleCnt = Number(resp?.possible_match_count || 0);
      const unknownCnt = Number(resp?.unknown_count || 0);
      const totalCnt   = Number(resp?.total_count || logs.length);

      setResults(logs);
      setSummary({
        known_count: knownCnt,
        possible_match_count: possibleCnt,
        unknown_count: unknownCnt,
        total_count: totalCnt,
      });
      setSelectedLogs(initialSelectedAuditIdsFromResults(logs, resp?.confidence_score));
      preloadAuditImages(logs);

      if (totalCnt > 0) {
        showMessage(
          `Found ${totalCnt} result(s): ${knownCnt} matched, ${possibleCnt} possible match, ${unknownCnt} unmatched`,
          'success'
        );
      } else {
        showMessage('No matching audit logs found', 'info');
      }
    } catch (err) {
      console.error(err);
      showMessage(err.message || 'Failed to search audit logs', 'error');
      setResults([]);
      setSummary({
        known_count: 0,
        possible_match_count: 0,
        unknown_count: 0,
        total_count: 0,
      });
    } finally {
      setSearchLoading(false);
      setResultsLoading(false);
      setHasSearched(true);
      setTimeout(() => { skipAutoRef.current = false; }, 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFaceImage, personThumbnail, firstLeft, firstRight, camera, threshold, preloadAuditImages]);

  // ── Checkbox ──────────────────────────────────────────────────────────────
  const handleCheckboxChange = (auditId, checked) => {
    const id = String(auditId || '');
    if (!id) return;
    // Single-select behavior: selecting one result clears previous selection.
    setSelectedLogs(checked ? new Set([id]) : new Set());
  };

  // ── Rematch & Mark Attendance ─────────────────────────────────────────────
  const handleRematchAndMark = async () => {
    if (selectedLogs.size === 0 || !personId) {
      showMessage('Please select logs and ensure person ID is available', 'warning'); return;
    }

    const isUnmatched = (log) =>
      log.find_person_source === 'unmatched' ||
      log.confidence_level === 'no_match' ||
      log.unknown_match_similarity !== undefined;

    const unmatchedIds = Array.from(selectedLogs).filter((id) => {
      const log = allLogsById.get(id);
      return log && isUnmatched(log);
    });

    if (unmatchedIds.length === 0) {
      showMessage('Please select unmatched logs to rematch', 'warning'); return;
    }

    try {
      setRematchLoading(true);
      const personName = missingEvent?.person_name || '';
      const action     = missingEvent?.action === 'check_in' ? 'check_in' : 'check_out';
      const thresh     = parseFloat(threshold) || 0.3;

      const rematchResp = await unmatchToMatch(unmatchedIds, personId, personName, thresh);
      if (!rematchResp.success) {
        showMessage(rematchResp.message || 'Failed to rematch logs', 'error'); return;
      }

      const markResults = await Promise.allSettled(
        unmatchedIds.map((auditId) =>
          markAuditAttendance({
            audit_id: auditId,
            person_id: personId,
            person_name: personName,
            employee_code: missingEvent?.employee_code || '',
            action,
          })
        )
      );
      const successCnt = markResults.filter((r) => r.status === 'fulfilled' && r.value?.attendance).length;
      const failedCnt  = markResults.length - successCnt;

      if (successCnt > 0) {
        showMessage(
          `Successfully rematched and marked attendance for ${successCnt} log(s)` +
            (failedCnt > 0 ? `, ${failedCnt} failed` : ''),
          'success'
        );
        onClose();
        onSuccess();
      } else {
        showMessage('Rematched but failed to mark attendance', 'warning');
        setSelectedLogs(new Set());
        await handleSearch();
      }
    } catch (err) {
      console.error(err);
      showMessage(err.message || 'Failed to rematch and mark attendance', 'error');
    } finally {
      setRematchLoading(false);
    }
  };

  // ── Mark Attendance (matched logs only) ───────────────────────────────────
  const handleMarkAttendance = async () => {
    if (typeof onOpenManualMarkAttendance === 'function') {
      if (!personId || !missingEvent || !firstLeft || !firstRight) {
        showMessage('Missing entry context is incomplete. Please retry.', 'warning');
        return;
      }
      onOpenManualMarkAttendance({
        personId,
        personName: missingEvent?.person_name || '',
        employeeCode: missingEvent?.employee_code || '',
        companyId: missingEvent?.company_id || '',
        missingAction: missingEvent?.action === 'check_in' ? 'check_in' : 'check_out',
        windowStart: firstLeft?.created_at || null,
        windowEnd: firstRight?.created_at || null,
      });
      return;
    }

    if (selectedLogs.size === 0 || !personId) {
      showMessage('Please select logs and ensure person ID is available', 'warning'); return;
    }

    const isMatched = (log) =>
      log.find_person_source === 'known' ||
      log.find_person_source === 'possible_match' ||
      log.confidence_level === 'possible_match' ||
      (log.success === true &&
        log.find_person_source !== 'unmatched' &&
        log.confidence_level !== 'no_match');

    const matchedIds = Array.from(selectedLogs).filter((id) => {
      const log = allLogsById.get(id);
      return log && isMatched(log);
    });

    if (matchedIds.length === 0) {
      showMessage('Please select matched logs to mark attendance', 'warning'); return;
    }

    try {
      setMarkLoading(true);
      const personName = missingEvent?.person_name || '';
      const action     = missingEvent?.action === 'check_in' ? 'check_in' : 'check_out';

      const markResults = await Promise.allSettled(
        matchedIds.map((auditId) => {
          const log = allLogsById.get(auditId);
          return markAuditAttendance({
            audit_id: auditId,
            person_id: personId,
            person_name: personName,
            employee_code: missingEvent?.employee_code || '',
            action,
            is_possible_match: log?.confidence_level === 'possible_match',
          });
        })
      );
      const successCnt = markResults.filter((r) => r.status === 'fulfilled' && r.value?.attendance).length;
      const failedCnt  = markResults.length - successCnt;

      if (successCnt > 0) {
        showMessage(
          `Successfully marked attendance for ${successCnt} log(s)` +
            (failedCnt > 0 ? `, ${failedCnt} failed` : ''),
          'success'
        );
        onClose();
        onSuccess();
      } else {
        showMessage('Failed to mark attendance for selected logs', 'error');
      }
    } catch (err) {
      console.error(err);
      showMessage(err.message || 'Failed to mark attendance', 'error');
    } finally {
      setMarkLoading(false);
    }
  };

  // ── Helpers for result buttons ────────────────────────────────────────────
  const isUnmatchedLog = (log) =>
    log.find_person_source === 'unmatched' ||
    log.confidence_level === 'no_match' ||
    log.unknown_match_similarity !== undefined;

  const isMatchedLog = (log) =>
    log.find_person_source === 'known' ||
    log.find_person_source === 'possible_match' ||
    log.confidence_level === 'possible_match' ||
    (log.success === true && !isUnmatchedLog(log));

  const matchedResults = results.filter(isMatchedLog);
  const allLogsById = new Map();
  [...results, ...rangeUnmatchedLogs].forEach((log) => {
    if (!log?.audit_id) return;
    allLogsById.set(String(log.audit_id), log);
  });

  const hasUnmatched = rangeUnmatchedLogs.length > 0 || results.some(isUnmatchedLog);
  const hasMatched = matchedResults.length > 0;

  const selectedUnmatchedCount = Array.from(selectedLogs).filter((id) => {
    const log = allLogsById.get(id);
    return log && isUnmatchedLog(log);
  }).length;

  const selectedMatchedCount = Array.from(selectedLogs).filter((id) => {
    const log = allLogsById.get(id);
    return log && isMatchedLog(log);
  }).length;

	  const searched = hasSearched;
	  const DISPLAY_COLUMNS = isMdUp ? 6 : isSmUp ? 2 : 1;
	  const MAX_VISIBLE_ROWS = isSmUp ? 3 : 2;

	  const getScrollableContainerSx = (count) => {
	    const rows = Math.ceil((Number(count) || 0) / DISPLAY_COLUMNS);
	    const shouldScroll = rows > MAX_VISIBLE_ROWS;
	    return {
	      border: '1px solid',
	      borderColor: 'divider',
	      borderRadius: 1,
	      p: { xs: 1.25, sm: 2 },
	      maxHeight: shouldScroll
	        ? { xs: fullScreen ? '45dvh' : '50vh', sm: '520px', md: '780px' }
	        : 'none',
	      overflowY: shouldScroll ? 'auto' : 'visible',
	    };
	  };

	  // ── Render ────────────────────────────────────────────────────────────────
	  return (
	    <Dialog
	      open={open}
	      onClose={onClose}
	      fullScreen={fullScreen}
	      maxWidth="lg"
	      fullWidth
	      PaperProps={{
	        sx: {
	          maxHeight: { xs: '100dvh', sm: '90vh' },
	          height: { xs: '100dvh', sm: 'auto' },
	          m: { xs: 0, sm: 2 },
	          width: { xs: '100%', sm: 'auto' },
	          borderRadius: { xs: 0, sm: 2 },
	        },
	      }}
	    >
	      <DialogTitle sx={{ py: { xs: 1.5, sm: 2 }, pr: { xs: 1, sm: 2 } }}>
	        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
	          <Typography variant="h6" sx={{ minWidth: 0, pr: 1, wordBreak: 'break-word' }}>
	            Search Audit Logs for Missing Entry
	          </Typography>
	          <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
	            <Close />
	          </IconButton>
	        </Box>
	      </DialogTitle>

	      <DialogContent
	        dividers
	        sx={{
	          p: { xs: 2, sm: 3 },
	          maxHeight: { xs: 'calc(100dvh - 160px)', sm: 'calc(90vh - 200px)' },
	          overflowY: 'auto',
	        }}
	      >
	        <Stack spacing={{ xs: 2, sm: 3 }}>
	          <Typography variant="body2" color="text.secondary">
	            Search audit logs between the first real event before and after this missing entry,
	            filtered by camera type and similarity threshold.
	          </Typography>

          {/* ── Person Image + Angle Selection ─────────────────────────── */}
          {personThumbnail && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Person Image</Typography>
              <Box
                sx={{
                  width: '100%', maxWidth: 200, height: 200,
                  borderRadius: 2, overflow: 'hidden',
                  border: '1px solid', borderColor: 'divider',
                }}
              >
                <Box
                  component="img"
                  src={selectedFaceImage || personThumbnail}
                  alt="Person"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>

              {personFaces.length > 0 && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Face Angle</InputLabel>
                  <Select
                    value={selectedAngle}
                    label="Face Angle"
                    onChange={(e) => handleAngleChange(e.target.value)}
                    disabled={searchLoading}
                  >
                    {personFaces.map((face) => (
                      <MenuItem key={face.face_id} value={face.face_id}>
                        {formatAngleLabel(face.face_angle, face.metadata || {})}
                        {face.face_angle === 'front' && ' (Recommended)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}

          {/* ── Missing Entry Info ─────────────────────────────────────── */}
          {missingEvent && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Missing Entry Information</Typography>
              <Stack spacing={1}>
                <Chip
                  label={missingEvent.action === 'check_in' ? 'Missing In' : 'Missing Out'}
                  color={missingEvent.action === 'check_in' ? 'success' : 'error'}
                  size="small"
                  sx={{ width: 'fit-content' }}
                />
                <Typography variant="body2" color="text.secondary">
                  Missing Entry Time: {formatDate(missingEvent.created_at, true, false)}
                </Typography>
              </Stack>
            </Box>
          )}

          {/* ── Time Range Info ────────────────────────────────────────── */}
          {firstLeft && firstRight && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Search Time Range</Typography>
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>From:</strong> {formatDate(firstLeft.created_at, true, false)}
                </Typography>
                <Typography variant="body2">
                  <strong>To:</strong> {formatDate(firstRight.created_at, true, false)}
                </Typography>
              </Stack>
            </Box>
          )}

          {/* ── Camera Filter ──────────────────────────────────────────── */}
          {camera && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Camera Filter</Typography>
              <Chip
                label={camera === 'IN' ? 'IN Camera' : 'OUT Camera'}
                color={camera === 'IN' ? 'primary' : 'secondary'}
                size="small"
                sx={{ width: 'fit-content' }}
              />
            </Box>
          )}

          {/* ── Similarity Threshold ───────────────────────────────────── */}
	          <Box sx={{ width: '100%' }}>
	            <Box
	              sx={{
	                display: 'flex',
	                flexDirection: { xs: 'column', sm: 'row' },
	                alignItems: { xs: 'stretch', sm: 'flex-start' },
	                gap: { xs: 1, sm: 4 },
	              }}
	            >
	              <Typography
	                variant="body2"
	                color="text.secondary"
	                sx={{
	                  minWidth: 'fit-content',
	                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
	                  flexShrink: 0,
	                  lineHeight: { xs: '20px', sm: '40px' },
	                }}
	              >
	                Similarity Threshold: {parseFloat(threshold || 0).toFixed(2)}
	              </Typography>
	              <Box sx={{ flex: 1, minWidth: 0, maxWidth: { xs: '100%', sm: 350 }, mt: { xs: 0, sm: 0.5 } }}>
	                <Slider
	                  value={parseFloat(threshold || 0.3)}
	                  onChange={(_, v) => setThreshold(String(v))}
	                  min={0} max={1} step={0.01}
	                  marks={fullScreen
	                    ? [
	                      { value: 0, label: '0' },
	                      { value: 0.5, label: '0.5' },
	                      { value: 1, label: '1' },
	                    ]
	                    : [
	                      { value: 0, label: '0' },
	                      { value: 0.25, label: '0.25' },
	                      { value: 0.5, label: '0.5' },
	                      { value: 0.75, label: '0.75' },
	                      { value: 1, label: '1' },
	                    ]}
	                  sx={{
	                    width: '100%',
	                    '& .MuiSlider-thumb': { width: 10, height: 10, marginTop: '-3px' },
	                    '& .MuiSlider-markLabel': { fontSize: fullScreen ? '0.6rem' : '0.65rem', top: 20 },
	                    '& .MuiSlider-track':      { height: 4, marginTop: '-2px' },
	                    '& .MuiSlider-rail':       { height: 4, marginTop: '-2px' },
	                  }}
	                />
	              </Box>
	            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Value between 0.00 and 1.00 (higher = stricter match)
            </Typography>
          </Box>

          {/* ── Results ────────────────────────────────────────────────── */}
          {resultsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!resultsLoading && hasMatched && (
            <>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2">
                    Highly Recommended (Top Matches) ({matchedResults.length})
                  </Typography>
                  {selectedMatchedCount > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {selectedMatchedCount} matched selected
                    </Typography>
                  )}
                </Box>

                <Box sx={getScrollableContainerSx(matchedResults.length)}>
                  <Grid container spacing={2}>
                    {matchedResults.map((log, idx) => {
                      const logImage = log.audit_id ? peekAuditImage(log.audit_id) : null;
                      const capturedTs = getCapturedTimestamp(log);
                      const resultMeta = getResultMeta(log);
                      return (
                        <Grid
                          item xs={12} sm={6} md={2}
                          key={log.audit_id || `${log.created_at || 'na'}-${idx}`}
                        >
                          <AuditTile
                            log={log}
                            lazyLoad
                            capturedTs={capturedTs}
                            resultMeta={resultMeta}
                            onClick={
                              onLogClick
                                ? () => onLogClick({ log, stack: [{ log, image: logImage }] })
                                : undefined
                            }
                            onSearchClick={onSearchIconClick || undefined}
                            findPersonResultMode={true}
                            onCheckboxChange={handleCheckboxChange}
                            isChecked={log.audit_id ? selectedLogs.has(String(log.audit_id)) : false}
                            selectionVariant="radio"
                            isUnmatchedLog={false}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  Unmatched In Selected Time Range ({rangeUnmatchedLogs.length})
                </Typography>
                {selectedUnmatchedCount > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedUnmatchedCount} unmatched selected
                  </Typography>
                )}
              </Box>
              {rangeUnmatchedLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : rangeUnmatchedLogs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No unmatched logs found in this time range.
                </Typography>
              ) : (
                <Box sx={getScrollableContainerSx(rangeUnmatchedLogs.length)}>
                  <Grid container spacing={2}>
                    {rangeUnmatchedLogs.map((log, idx) => {
                      const logImage = log.audit_id ? peekAuditImage(log.audit_id) : null;
                      const capturedTs = getCapturedTimestamp(log);
                      const resultMeta = getResultMeta(log);
                      return (
                        <Grid
                          item xs={12} sm={6} md={2}
                          key={log.audit_id || `${log.created_at || 'na'}-${idx}`}
                        >
                          <AuditTile
                            log={log}
                            lazyLoad
                            capturedTs={capturedTs}
                            resultMeta={resultMeta}
                            onClick={
                              onLogClick
                                ? () => onLogClick({ log, stack: [{ log, image: logImage }] })
                                : undefined
                            }
                            onSearchClick={onSearchIconClick || undefined}
                            findPersonResultMode={true}
                            onCheckboxChange={handleCheckboxChange}
                            isChecked={log.audit_id ? selectedLogs.has(String(log.audit_id)) : false}
                            selectionVariant="radio"
                            isUnmatchedLog={true}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}
            </>
          )}

          {!resultsLoading && !hasMatched && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">
                  Unmatched In Selected Time Range ({rangeUnmatchedLogs.length})
                </Typography>
                {selectedUnmatchedCount > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedUnmatchedCount} unmatched selected
                  </Typography>
                )}
              </Box>
              {rangeUnmatchedLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : rangeUnmatchedLogs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No unmatched logs found in this time range.
                </Typography>
              ) : (
                <Box sx={getScrollableContainerSx(rangeUnmatchedLogs.length)}>
                  <Grid container spacing={2}>
                    {rangeUnmatchedLogs.map((log, idx) => {
                      const logImage = log.audit_id ? peekAuditImage(log.audit_id) : null;
                      const capturedTs = getCapturedTimestamp(log);
                      const resultMeta = getResultMeta(log);
                      return (
                        <Grid
                          item xs={12} sm={6} md={2}
                          key={log.audit_id || `${log.created_at || 'na'}-${idx}`}
                        >
                          <AuditTile
                            log={log}
                            lazyLoad
                            capturedTs={capturedTs}
                            resultMeta={resultMeta}
                            onClick={
                              onLogClick
                                ? () => onLogClick({ log, stack: [{ log, image: logImage }] })
                                : undefined
                            }
                            onSearchClick={onSearchIconClick || undefined}
                            findPersonResultMode={true}
                            onCheckboxChange={handleCheckboxChange}
                            isChecked={log.audit_id ? selectedLogs.has(String(log.audit_id)) : false}
                            selectionVariant="radio"
                            isUnmatchedLog={true}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}
            </Box>
          )}

	          {(hasUnmatched || hasMatched || (typeof onOpenManualMarkAttendance === 'function' && !!personId && !!missingEvent && !!firstLeft && !!firstRight)) && (
	            <Box
	              sx={{
	                mt: 2,
	                display: 'flex',
	                gap: 2,
	                justifyContent: 'flex-end',
	                flexDirection: { xs: 'column', sm: 'row' },
	                alignItems: { xs: 'stretch', sm: 'center' },
	              }}
	            >
	              {hasUnmatched && (
	                <Button
	                  variant="outlined"
	                  color="primary"
	                  onClick={handleRematchAndMark}
	                  disabled={rematchLoading || !personId || selectedUnmatchedCount === 0}
	                  startIcon={rematchLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
	                  fullWidth={fullScreen}
	                  sx={{ textTransform: 'none' }}
	                >
	                  {rematchLoading
	                    ? 'Processing...'
	                    : `Rematch & Mark Attendance${selectedUnmatchedCount > 0 ? ` (${selectedUnmatchedCount})` : ''}`}
	                </Button>
	              )}
	              {(hasMatched || typeof onOpenManualMarkAttendance === 'function') && (
	                <Button
	                  variant="contained"
	                  color="primary"
	                  onClick={handleMarkAttendance}
	                  disabled={
	                    markLoading ||
	                    !personId ||
	                    (typeof onOpenManualMarkAttendance !== 'function' && selectedMatchedCount === 0)
	                  }
	                  startIcon={markLoading ? <CircularProgress size={16} /> : <EventAvailable />}
	                  fullWidth={fullScreen}
	                  sx={{ textTransform: 'none', backgroundColor: primary }}
	                >
	                  {markLoading
	                    ? 'Marking...'
	                    : (typeof onOpenManualMarkAttendance === 'function'
	                      ? 'Mark Attendance'
	                      : `Mark Attendance${selectedMatchedCount > 0 ? ` (${selectedMatchedCount})` : ''}`)}
	                </Button>
	              )}
	            </Box>
	          )}

          {/* No results yet */}
          {!resultsLoading && results.length === 0 && !searched && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No results yet. Click "Find Person" to search.
              </Typography>
            </Box>
          )}

          {/* Searched but nothing found */}
          {!resultsLoading && results.length === 0 && searched && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No matched results found for selected threshold.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

	      <DialogActions
	        sx={{
	          px: { xs: 2, sm: 3 },
	          py: { xs: 1.5, sm: 2 },
	          flexDirection: { xs: 'column-reverse', sm: 'row' },
	          alignItems: { xs: 'stretch', sm: 'center' },
	          gap: { xs: 1, sm: 0 },
	        }}
	      >
	        <Button onClick={onClose} fullWidth={fullScreen} sx={{ textTransform: 'none' }}>
	          Cancel
	        </Button>
	        {/* <Button
	          variant="contained"
	          onClick={handleSearch}
	          disabled={searchLoading || !personThumbnail}
	          startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <Search />}
	          fullWidth={fullScreen}
	          sx={{
	            textTransform: 'none',
	            backgroundColor: primary,
	            '&:hover': { backgroundColor: primary, opacity: 0.9 },
	          }}
	        >
	          {searchLoading ? 'Searching...' : 'Find Person'}
	        </Button>
	        */}
	      </DialogActions>
	    </Dialog>
	  );
	};

export default MissingEntrySearchModal;
